import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Tooltip, Typography } from 'antd';
import {
  CalendarOutlined,
  FieldTimeOutlined,
  MessageOutlined,
  UpSquareOutlined,
  WarningOutlined,
  FileExclamationOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import { useGetCountries } from 'api/countries/use-get-countries.ts';
import { addressStatus } from 'shared/utils/address-status.ts';
import { findCountriesById } from 'shared/utils/countries';
import { parseAddressExtra } from 'shared/utils/parse-address-extra.ts';
import { dateDifferenceInDays } from 'shared/utils/time/date-difference-in-days.ts';
import { formatDate } from 'shared/utils/time/format-date.ts';
import { isExpiredDate } from 'shared/utils/time/is-expired-period-of-date.ts';
import { ImportantCargoDocumentFlags } from 'components/documents/important-cargo-document-flags';
import styles from './cargo-address-col.module.scss';
import { TimePeriodMessage } from './components/time-period-message';
import type { ICargoAddressColProps } from './types';

const LiftIcon = () => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('cargo:address_truck_with_lift')}>
      <UpSquareOutlined className={styles.icon} />
    </Tooltip>
  );
};

const BookingIcon = () => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('cargo:address_booking')}>
      <CalendarOutlined className={styles.icon} />
    </Tooltip>
  );
};

const FixTimeIcon = () => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('cargo:address_fix_time')}>
      <FieldTimeOutlined className={styles.icon} />
    </Tooltip>
  );
};

const AttentionIcon = () => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('cargo:address_attention')}>
      <WarningOutlined className={styles.icon} />
    </Tooltip>
  );
};

export const CargoAddressCol: React.FC<ICargoAddressColProps> = (props) => {
  const {
    address,
    customAddress,
    dateFrom,
    dateTo,
    documents,
    needCalculateDate = false,
    actualDate,
    isOverdueVisible = true,
    showCmrWarning = false,
  } = props;
  const { t } = useTranslation();
  const { data: countries } = useGetCountries();
  const country = findCountriesById(address.countryId, countries);

  const { withLift, withBooking, withFixTime, withAttention } = parseAddressExtra(address.extraData);

  const timePeriod = needCalculateDate ? isExpiredDate(dateFrom, dateTo) : 'ok';
  const dateDiff = dateDifferenceInDays(dateTo, actualDate);

  return (
    <>
      {needCalculateDate && <TimePeriodMessage timePeriod={timePeriod} />}

      <Typography.Text className={styles.address}>
        <strong>{country?.code}</strong>, {address.postCode} {address.city}, {address.address}{' '}
        {addressStatus(address.googleLocationType, {
          withoutLike: true,
          detectionType: address.detectionType,
        })}
      </Typography.Text>

      {customAddress ? (
        <Typography.Text className={styles.customAddress}>
          {t('cargo:custom')}: {customAddress.postCode} {customAddress.city}, {customAddress.address}{' '}
          {addressStatus(customAddress.googleLocationType, {
            withoutLike: true,
            detectionType: customAddress.detectionType,
          })}
        </Typography.Text>
      ) : null}
      <Typography.Text className={styles.date}>
        {formatDate(dateFrom)} / {formatDate(dateTo)}
      </Typography.Text>
      {isOverdueVisible && dateDiff > 0 && (
        <Typography.Text className={classNames(styles.date, styles.overdue)}>
          {t('cargo:overdue', { count: dateDiff })}
        </Typography.Text>
      )}
      <Flex justify='space-between'>
        {documents && (
          <div>
            <ImportantCargoDocumentFlags documents={documents} />{' '}
          </div>
        )}
        {showCmrWarning ? (
          <Typography.Text className={styles.cmrNotReceived}>
            <FileExclamationOutlined />
            {t('cargo:cmr_not_received')}
          </Typography.Text>
        ) : null}
        <Flex justify='flex-end' align='center' className={styles.extraData}>
          {withLift ? <LiftIcon /> : null}
          {withBooking ? <BookingIcon /> : null}
          {withFixTime ? <FixTimeIcon /> : null}
          {withAttention ? <AttentionIcon /> : null}
          {address.description ? (
            <Tooltip title={address.description}>
              <MessageOutlined className={styles.icon} />
            </Tooltip>
          ) : null}
        </Flex>
      </Flex>
    </>
  );
};
